import { useEffect, useState } from "react";
import styled from "styled-components/macro";
import {
  BoxContent,
  Box,
  BoxNavRight,
  BoxNavButton,
  BoxHeader,
  BoxTitle,
  BoxFooter,
  BoxNavLeft,
} from "../Box";
import Button from "../Button";
import Stack from "../Stack";
import usePlan from "../../hooks/use-plan";
import { useSkipioApi } from "../../api";
import { StackingBoxModal } from "../StackingBox";
import { Close, SkipioLogo, Check } from "../Icons";
import Bar from "../Bar";
import MessagesKeyframes from "../MessagesKeyframes";
import { THEME_COLOR, SUCCESS_COLOR } from "../../vars";
import Title from "../Title";
import { HelpText } from "../Input";
import { useSkipioQuery } from "../../hooks/use-query";
import { renderSubscriptionExplanation } from "./util";
import { useQuery } from "@tanstack/react-query";
import { fetchUser } from "../../api/users";

const STEPS = {
  initial: 0,
  confirm: 1,
  complete: 2,
};

export default function EndTrialPrompt() {
  const [step, setStep] = useState(STEPS.initial);
  const plan = usePlan();
  const skipioApi = useSkipioApi();
  const user = useQuery({
    queryKey: ["users", "me"],
    queryFn: fetchUser,
    select: (queryData) => queryData.data,
  }).data!;
  const [showing, setShowing] = useState(false);
  const [dismissed, setDismissed] = useState(false);

  const { subscription } = useSkipioQuery({
    queryKey: ["billing"],
    suspense: false,
  }).data || { subscription: null };

  const isTrial = subscription && subscription.status === "trialing";

  useEffect(() => {
    if (dismissed) return;

    if (isTrial && plan.percentOfMessagesUsed >= 75) {
      setShowing(true);
    }
  }, [isTrial, plan.percentOfMessagesUsed]);

  async function save() {
    await skipioApi.accountSkipioPlan.finishTrial.put();
    setStep(STEPS.complete);
  }

  if (!showing) return null;

  return (
    <StackingBoxModal height={400}>
      <Box elevated>
        <BoxHeader stacked white>
          <BoxTitle>You are close to your limit</BoxTitle>
          <BoxNavRight>
            <BoxNavButton
              onClick={() => {
                setShowing(false);
                setDismissed(false);
              }}
            >
              <Close />
            </BoxNavButton>
          </BoxNavRight>
        </BoxHeader>
        <BoxContent>
          <Stack>
            <Bar
              used={user.skipioPlan.monthlySmsMessagesSent}
              max={user.account.maximumMonthlySmsMessages}
              percent={plan.percentOfMessagesUsed}
            />
            <MessagesKeyframes
              messages={[
                {
                  body: `😅`,
                  duration: 1,
                },
                {
                  body: `You have used ${plan.percentOfMessagesUsed}% of your monthly outbound messages. During an active trial, messages beyond that limit will not send.`,
                  duration: 1,
                },
                {
                  body: `To enable overages and continue sending messages, you can choose to end the trial period early.`,
                  duration: 1,
                },
              ]}
            />
            <Center>
              <Stack>
                <Button
                  onClick={() => {
                    setStep(STEPS.confirm);
                  }}
                >
                  Finish Trial Early
                </Button>
              </Stack>
            </Center>
          </Stack>
        </BoxContent>
      </Box>
      {step >= STEPS.confirm && (
        <Box elevated>
          <BoxContent
            style={{
              alignItems: "center",
              justifyContent: "center",
              display: "flex",
            }}
          >
            <Center>
              <Stack space={5}>
                <SkipioLogo
                  style={{
                    color: THEME_COLOR,
                    width: "64px",
                    height: "64px",
                  }}
                />
                <Stack space={2}>
                  <Title>Finish Trial Early</Title>
                  <div
                    style={{
                      display: "inline-flex",
                      justifyContent: "center",
                      alignItems: "center",
                    }}
                  >
                    <HelpText noIcon style={{ textAlign: "center" }}>
                      {renderSubscriptionExplanation({
                        plan: plan.skipioPlan,
                        changeType: "trialCompletion",
                      })}
                    </HelpText>
                  </div>
                </Stack>
              </Stack>
            </Center>
          </BoxContent>
          <BoxFooter>
            <BoxNavLeft>
              <Button onClick={() => setStep(STEPS.initial)} variant="link">
                Back
              </Button>
            </BoxNavLeft>
            <BoxNavRight>
              <Button
                onClick={async () => {
                  await save();
                }}
              >
                Finish Trial
              </Button>
            </BoxNavRight>
          </BoxFooter>
        </Box>
      )}
      {step >= STEPS.complete && (
        <Box elevated>
          <BoxContent
            style={{
              alignItems: "center",
              justifyContent: "center",
              display: "flex",
            }}
          >
            <div style={{ textAlign: "center" }}>
              <Stack space={10}>
                <Stack>
                  <Check
                    style={{
                      color: SUCCESS_COLOR,
                      width: "48px",
                      height: "48px",
                    }}
                  />
                  <Title>Your plan has been updated</Title>
                </Stack>
                <Button
                  onClick={() => {
                    window.location.href = window.location.pathname;
                  }}
                >
                  Return to the app
                </Button>
              </Stack>
            </div>
          </BoxContent>
        </Box>
      )}
    </StackingBoxModal>
  );
}

const Center = styled.div`
  text-align: center;
`;
